import { AssetFileModel, AssetModel, SystemTypePpu } from '@customer/store/apis/assetsApi';
import styles from './AssetPanelConfiguration.module.scss';
import { useTranslation } from 'react-i18next';
import { useCallback, useMemo } from 'react';
import { GridControlled } from '@customer/components/tables/grid/GridControlled';
import { useLocaleFormatter } from '@customer/shared/hooks/useLocaleFormatter';
import CasAccordion from '@customer/components/accordions/accordion/CasAccordion';
import { AccordionStatus } from '@customer/components/accordions/types';
import AccordionHeader from '@customer/components/accordions/header/AccordionHeader';
import { useGetConfigurationGroupName } from '@customer/store/apis/configurationGroupsApi';
import { AssetConfigurationAccordion } from '@customer/store/slices/globalSlice';
import { useAppSelector } from '@customer/store/hooks';
import { useAssetGeofenceTableData } from '@customer/hooks/assets/useAssetGeofenceTableData';
import { AssetNameData, useAssetNameTableData } from '@customer/hooks/assets/useAssetNameTableData';
import AssetInfo, { AssetInfoRow } from '@customer/components/asset-data/asset-info/AssetInfo';
import { AccordionTable } from '@customer/components/accordions/table/AccordionTable';
import {
  columnDefAssetFilesAppliedAlt,
  columnDefAssetNamesName,
  columnDefAssetNamesSource,
  lockColumn,
} from '@customer/components/tables/grid/columnDefs';
import { ColDef, RowClassParams, RowClassRules, ValueFormatterParams } from 'ag-grid-community';
import GeofenceStatus from '@customer/components/asset-data/geofence-status/GeofenceStatus';

const gridSliceKeyConfiguration = 'AssetPanelConfig';
const gridSliceKeyName = 'AssetPanelName';

const getRowIdConfig = (data: AssetFileModel) => data.name;
const getRowIdAssetName = (data: AssetNameData) => data.name;

const configRowClassRules: RowClassRules<AssetFileModel> = {
  'dm-bold-row': (params: RowClassParams<AssetFileModel>) => !params.data?.app,
};

export interface AssetPanelConfigurationProps {
  asset: AssetModel;
}

export function AssetPanelConfiguration({ asset }: AssetPanelConfigurationProps) {
  const { t } = useTranslation();
  const { showTimeAndDateFromIso } = useLocaleFormatter();

  const globalData = useAppSelector(state => state.global);

  // Satisfies both valueFormatter and getQuickFilterText
  const statusValueFormatter = useCallback(
    ({ value }: ValueFormatterParams<AssetFileModel, string>): string => {
      return t(value ?? 'Unknown');
    },
    [t],
  );

  const columnDefsConfig: ColDef<AssetFileModel>[] = useMemo(
    () => [
      { field: 'name', headerName: t('PackageName') },
      { ...columnDefAssetFilesAppliedAlt(t) },
      {
        field: 'stat',
        headerName: t('Status'),
        valueFormatter: statusValueFormatter,
        getQuickFilterText: statusValueFormatter,
      },
    ],
    [t, statusValueFormatter],
  );

  const geofenceData = useAssetGeofenceTableData({ asset });

  const assetNameData = useAssetNameTableData({ asset });

  const columnDefsAssetNames: ColDef<AssetNameData>[] = useMemo(
    () => [{ ...columnDefAssetNamesName(t) }, { ...columnDefAssetNamesSource(t) }],
    [t],
  );

  const configGroupName = useGetConfigurationGroupName(asset.cfgGrp);

  const infoRows: AssetInfoRow[] = [
    {
      label: t('Device ID'),
      value: asset.did,
      emphasis: true,
    },
    {
      label: t('ConfigurationGroup'),
      value: configGroupName ?? '',
      displayCondition: asset.hw !== SystemTypePpu,
    },
    {
      label: t('Updated'),
      value: showTimeAndDateFromIso(asset.tsCfg),
      displayCondition: asset.hw !== SystemTypePpu,
    },
  ];

  return (
    <div className={styles.container}>
      <AssetInfo rows={infoRows} />

      {asset.hw !== SystemTypePpu && (
        <div className={styles.accordions}>
          <CasAccordion
            key={AssetConfigurationAccordion.SoftwareStatus}
            defaultExpanded={
              globalData.sidePanelDefaultAccordion === AssetConfigurationAccordion.SoftwareStatus
            }
            status={asset.stsCfg ? AccordionStatus.Healthy : AccordionStatus.Danger}
            icon={asset.stsCfg ? 'good' : 'caution'}
            header={
              <AccordionHeader
                title={t('SoftwareStatus')}
                subtitle={asset.stsCfg ? t('ConfigurationStatusGood') : t('OpenToViewDetails')}
              />
            }
          >
            <AccordionTable hasRows={!!asset.files.length} noRowsMessage={t('No files available')}>
              <GridControlled<AssetFileModel>
                autoheight={true}
                sliceKey={gridSliceKeyConfiguration}
                rowData={asset.files}
                columnDefs={columnDefsConfig}
                defaultColDef={lockColumn}
                getRowId={getRowIdConfig}
                rowClassRules={configRowClassRules}
              />
            </AccordionTable>
          </CasAccordion>

          <GeofenceStatus
            geofenceData={geofenceData}
            good={asset.stsGeo}
            expanded={
              globalData.sidePanelDefaultAccordion === AssetConfigurationAccordion.GeofenceStatus
            }
          />

          {!asset.stsNam && (
            <CasAccordion
              key={AssetConfigurationAccordion.NameStatus}
              defaultExpanded={true}
              status={AccordionStatus.Danger}
              icon="information"
              header={
                <AccordionHeader title={t('AssetNameMismatch')} subtitle={t('OpenToViewDetails')} />
              }
            >
              <AccordionTable
                hasRows={!!assetNameData.length}
                noRowsMessage={t('GeofenceStatusUnavailable')}
              >
                <GridControlled<AssetNameData>
                  autoheight={true}
                  sliceKey={gridSliceKeyName}
                  rowData={assetNameData}
                  columnDefs={columnDefsAssetNames}
                  defaultColDef={lockColumn}
                  getRowId={getRowIdAssetName}
                />
              </AccordionTable>
            </CasAccordion>
          )}
        </div>
      )}
    </div>
  );
}

export default AssetPanelConfiguration;
